import React, { useState, useEffect } from 'react';
import './ReportList.css';
import {
    getCustomerListReport,
    downloadCustomerListExcel,
    getProductListReport,
    downloadProductListExcel,
    getSalesReport,
    downloadSalesReportPDF,
    getRepairReport,
    downloadRepairReportPDF
} from '../../services/reportService';
import { format } from 'date-fns';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const ReportList = () => {
    // State variables
    const [activeTab, setActiveTab] = useState('repairs'); // ✅ Set 'repairs' as the default

    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [salesData, setSalesData] = useState(null);
    const [repairData, setRepairData] = useState(null);
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
    const [endDate, setEndDate] = useState(new Date());
    const [loading, setLoading] = useState({
        customers: false,
        products: false,
        sales: false,
        repairs: false
    });
    const [downloadURL, setDownloadURL] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(3);

    // Colors for charts - black and white theme with gray shades
    const COLORS = ['#000000', '#333333', '#666666', '#999999', '#CCCCCC'];

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top when changing tabs
    }, [activeTab]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top when changing tabs
        // Reset to first page when changing tabs
        setCurrentPage(1);

        if (activeTab === 'customers') {
            loadCustomers();
        } else if (activeTab === 'products') {
            loadProducts();
        } else if (activeTab === 'sales') {
            loadSalesReport();  // Auto-generate sales report
        } else if (activeTab === 'repairs') {
            loadRepairReport(); // Auto-generate repair report
        }
    }, [activeTab]);

    // Load customer list
    const loadCustomers = async () => {
        setLoading(prev => ({ ...prev, customers: true }));
        try {
            const result = await getCustomerListReport();
            setCustomers(result.data);
        } catch (error) {
            console.error("Error loading customers:", error);
        } finally {
            setLoading(prev => ({ ...prev, customers: false }));
        }
    };

    // Download customer list as Excel
    const handleDownloadCustomerExcel = async () => {
        try {
            await downloadCustomerListExcel();
        } catch (error) {
            console.error("Error downloading customer Excel:", error);
        }
    };

    // Load product list
    const loadProducts = async () => {
        setLoading(prev => ({ ...prev, products: true }));
        try {
            const result = await getProductListReport();
            setProducts(result.data);
        } catch (error) {
            console.error("Error loading products:", error);
        } finally {
            setLoading(prev => ({ ...prev, products: false }));
        }
    };

    // Download product list as Excel
    const handleDownloadProductExcel = async () => {
        try {
            await downloadProductListExcel();
        } catch (error) {
            console.error("Error downloading product Excel:", error);
        }
    };

    // Load sales report
    const loadSalesReport = async () => {
        if (!startDate || !endDate) {
            console.error("Error: Please select start and end dates.");
            return;
        }

        setLoading(prev => ({ ...prev, sales: true }));
        try {
            const formattedStartDate = format(startDate, 'yyyy-MM-dd');
            const formattedEndDate = format(endDate, 'yyyy-MM-dd');

            const result = await getSalesReport(formattedStartDate, formattedEndDate);
            if (result && result.success && result.data) {
                setSalesData(result.data);
            } else {
                setSalesData(null);
            }
        } catch (error) {
            console.error("Error loading sales report:", error);
            setSalesData(null);
        } finally {
            setLoading(prev => ({ ...prev, sales: false }));
        }
    };

    // Download sales report as PDF
    const handleDownloadSalesPDF = async () => {
        if (!startDate || !endDate) {
            return;
        }

        try {
            const formattedStartDate = format(startDate, 'yyyy-MM-dd');
            const formattedEndDate = format(endDate, 'yyyy-MM-dd');

            await downloadSalesReportPDF(formattedStartDate, formattedEndDate);
        } catch (error) {
            console.error("Error downloading sales PDF:", error);
        }
    };

    // Load repair report
    const loadRepairReport = async () => {
        if (!startDate || !endDate) {
            return;
        }

        setLoading(prev => ({ ...prev, repairs: true }));
        try {
            const formattedStartDate = format(startDate, 'yyyy-MM-dd');
            const formattedEndDate = format(endDate, 'yyyy-MM-dd');

            const result = await getRepairReport(formattedStartDate, formattedEndDate);
            setRepairData(result.data);
        } catch (error) {
            console.error("Error loading repair report:", error);
        } finally {
            setLoading(prev => ({ ...prev, repairs: false }));
        }
    };

    // Download repair report as PDF
    const handleDownloadRepairPDF = async () => {
        if (!startDate || !endDate) {
            console.error("Error: Please select start and end dates.");
            return;
        }

        try {
            const formattedStartDate = format(startDate, 'yyyy-MM-dd');
            const formattedEndDate = format(endDate, 'yyyy-MM-dd');

            const url = await downloadRepairReportPDF(formattedStartDate, formattedEndDate);
            setDownloadURL(url);
        } catch (error) {
            console.error("Error downloading repair PDF:", error);
        }
    };

    // Get the report title based on active tab
    const getReportTitle = () => {
        switch (activeTab) {
            case 'customers': return 'Customer List Report';
            case 'products': return 'Product List Report';
            case 'sales': return 'Sales Revenue Report';
            case 'repairs': return 'Repair Income Report';
            default: return 'Report';
        }
    };

    // Pagination functions
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;

    const getCurrentItems = (items) => {
        return items.slice(indexOfFirstRow, indexOfLastRow);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const renderPagination = (totalItems) => {
        const pageNumbers = [];
        for (let i = 1; i <= Math.ceil(totalItems / rowsPerPage); i++) {
            pageNumbers.push(i);
        }

        if (pageNumbers.length <= 1) return null;

        return (
            <div className="pagination">
                <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                >
                    &laquo;
                </button>

                {pageNumbers.map(number => (
                    <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`pagination-btn ${currentPage === number ? 'active' : ''}`}
                    >
                        {number}
                    </button>
                ))}

                <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === pageNumbers.length}
                    className="pagination-btn"
                >
                    &raquo;
                </button>

                <div className="pagination-info">
                    Page {currentPage} of {pageNumbers.length} ({totalItems} items)
                </div>
            </div>
        );
    };

    // Customer list content
    const renderCustomerList = () => {
        if (loading.customers) {
            return <div className="loading-spinner"></div>;
        }

        const currentCustomers = getCurrentItems(customers);

        return (
            <>
                <div className="action-bar">
                    <button className="btn btn-refresh" onClick={loadCustomers}>Refresh</button>
                    <button className="btn btn-download" onClick={handleDownloadCustomerExcel}>Download Excel</button>
                </div>

                {customers.length > 0 ? (
                    <>
                        <div className="table-wrapper">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>NIC</th>
                                    <th>Name</th>
                                    <th>Address</th>
                                    <th>Phone</th>
                                    <th>Gender</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentCustomers.map((customer) => (
                                    <tr key={customer.NIC}>
                                        <td>{customer.NIC}</td>
                                        <td>{`${customer.FirstName} ${customer.LastName}`}</td>
                                        <td>{customer.Address}</td>
                                        <td>{customer.ContactNumber}</td>
                                        <td>{customer.Gender}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        {renderPagination(customers.length)}
                    </>
                ) : (
                    <div className="empty-state">
                        <p>No customer data available. Click Refresh to load data.</p>
                    </div>
                )}
            </>
        );
    };

    // Product list content
    const renderProductList = () => {
        if (loading.products) {
            return <div className="loading-spinner"></div>;
        }

        const currentProducts = getCurrentItems(products);

        return (
            <>
                <div className="action-bar">
                    <button className="btn btn-refresh" onClick={loadProducts}>Refresh</button>
                    <button className="btn btn-download" onClick={handleDownloadProductExcel}>Download Excel</button>
                </div>

                {products.length > 0 ? (
                    <>
                        <div className="table-wrapper">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>Category</th>
                                    <th>Brand</th>
                                    <th>Price</th>
                                    <th>Status</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentProducts.map((product) => (
                                    <tr key={product.id}>
                                        <td>{product.id}</td>
                                        <td>{product.name}</td>
                                        <td>{product.category_name}</td>
                                        <td>{product.brand}</td>
                                        <td>Rs.{parseFloat(product.price || 0).toFixed(2)}</td>
                                        <td>
                                            <span className={`status-badge ${product.status === 1 ? 'active' : 'inactive'}`}>
                                                {product.status === 1 ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        {renderPagination(products.length)}
                    </>
                ) : (
                    <div className="empty-state">
                        <p>No product data available. Click Refresh to load data.</p>
                    </div>
                )}
            </>
        );
    };

    // Sales report content
    const renderSalesReport = () => {
        const currentDailySales = salesData ? getCurrentItems(salesData.dailySales) : [];

        return (
            <>
                <div className="date-action-bar">
                    <div className="date-range">
                        <div className="date-field">
                            <label>Start:</label>
                            <input
                                type="date"
                                value={format(startDate, 'yyyy-MM-dd')}
                                onFocus={(e) => e.target.blur()}  // Prevents auto-focus
                                onChange={(e) => setStartDate(new Date(e.target.value))}
                            />
                        </div>

                        <div className="date-separator">to</div>

                        <div className="date-field">
                            <label>End:</label>
                            <input
                                type="date"
                                value={format(endDate, 'yyyy-MM-dd')}
                                onFocus={(e) => e.target.blur()}  // Prevents auto-focus
                                onChange={(e) => setEndDate(new Date(e.target.value))}
                            />
                        </div>
                    </div>

                    <div className="action-buttons">
                        <button className="btn btn-generate" onClick={loadSalesReport}>Generate</button>
                        <button className="btn btn-download" onClick={handleDownloadSalesPDF}>Download PDF</button>
                    </div>
                </div>

                {loading.sales ? (
                    <div className="loading-spinner"></div>
                ) : salesData ? (
                    <div className="report-content">
                        {/* Summary cards */}
                        <div className="summary-cards">
                            <div className="summary-card">
                                <h3>Total Sales</h3>
                                <p className="value">Rs.{parseFloat(salesData.summary.totalSales).toFixed(2)}</p>
                            </div>
                            <div className="summary-card">
                                <h3>Total Invoices</h3>
                                <p className="value">{salesData.summary.totalInvoices}</p>
                            </div>
                            <div className="summary-card">
                                <h3>Total Discounts</h3>
                                <p className="value">Rs.{parseFloat(salesData.summary.totalDiscounts).toFixed(2)}</p>
                            </div>
                            <div className="summary-card">
                                <h3>Avg. Sale</h3>
                                <p className="value">Rs.{parseFloat(salesData.summary.averageSalePerInvoice).toFixed(2)}</p>
                            </div>
                        </div>

                        {/* Sales chart */}
                        <div className="chart-container">
                            <h3>Daily Sales</h3>
                            <div className="chart">
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart
                                        data={salesData.dailySales.map(day => ({
                                            ...day,
                                            date: new Date(day.date).toLocaleDateString(),
                                        }))}
                                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                        <YAxis tick={{ fontSize: 10 }} />
                                        <Tooltip contentStyle={{ fontSize: 10 }} />
                                        <Legend wrapperStyle={{ fontSize: 10 }} />
                                        <Line
                                            type="monotone"
                                            dataKey="total_sales"
                                            name="Gross Sales"
                                            stroke="#000000"
                                            activeDot={{ r: 8 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="final_sales"
                                            name="Net Sales"
                                            stroke="#666666"
                                            strokeDasharray="5 5"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Sales data table */}
                        <div className="table-wrapper">
                            <h3>Daily Sales Details</h3>
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Invoices</th>
                                    <th>Gross Sales</th>
                                    <th>Discounts</th>
                                    <th>Net Sales</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentDailySales.map((day, index) => (
                                    <tr key={index}>
                                        <td>{new Date(day.date).toLocaleDateString()}</td>
                                        <td>{day.total_invoices}</td>
                                        <td>Rs.{parseFloat(day.total_sales).toFixed(2)}</td>
                                        <td>Rs.{parseFloat(day.total_discounts).toFixed(2)}</td>
                                        <td>Rs.{parseFloat(day.final_sales).toFixed(2)}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        {salesData.dailySales.length > 0 && renderPagination(salesData.dailySales.length)}
                    </div>
                ) : (
                    <div className="empty-state">
                        <p>Select a date range and click Generate to view sales report.</p>
                    </div>
                )}
            </>
        );
    };

    // Repair report content
    const renderRepairReport = () => {
        const currentDailyRepairs = repairData ? getCurrentItems(repairData.dailyRepairs) : [];

        return (
            <>
                <div className="date-action-bar">
                    <div className="date-range">
                        <div className="date-field">
                            <label>Start:</label>
                            <input
                                type="date"
                                value={format(startDate, 'yyyy-MM-dd')}
                                onFocus={(e) => e.target.blur()}  // Prevents auto-focus
                                onChange={(e) => setStartDate(new Date(e.target.value))}
                            />
                        </div>

                        <div className="date-separator">to</div>

                        <div className="date-field">
                            <label>End:</label>
                            <input
                                type="date"
                                value={format(endDate, 'yyyy-MM-dd')}
                                onFocus={(e) => e.target.blur()}  // Prevents auto-focus
                                onChange={(e) => setEndDate(new Date(e.target.value))}
                            />
                        </div>
                    </div>

                    <div className="action-buttons">
                        <button className="btn btn-generate" onClick={loadRepairReport}>Generate</button>
                        <button className="btn btn-download" onClick={handleDownloadRepairPDF}>Download PDF</button>
                    </div>
                </div>

                {loading.repairs ? (
                    <div className="loading-spinner"></div>
                ) : repairData ? (
                    <div className="report-content">
                        {/* Summary cards */}
                        <div className="summary-cards">
                            <div className="summary-card">
                                <h3>Total Income</h3>
                                <p className="value">Rs.{parseFloat(repairData.summary.totalIncome || 0).toFixed(2)}</p>
                            </div>
                            <div className="summary-card">
                                <h3>Total Repairs</h3>
                                <p className="value">{repairData.summary.totalRepairs}</p>
                            </div>
                            <div className="summary-card">
                                <h3>Parts Cost</h3>
                                <p className="value">Rs.{parseFloat(repairData.summary.totalPartsCost || 0).toFixed(2)}</p>
                            </div>
                            <div className="summary-card">
                                <h3>Labor Income</h3>
                                <p className="value">Rs.{parseFloat(repairData.summary.totalLaborIncome || 0).toFixed(2)}</p>
                            </div>
                        </div>

                        {/* Repair chart */}
                        <div className="chart-container">
                            <h3>Daily Repair Income</h3>
                            <div className="chart">
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart
                                        data={repairData.dailyRepairs.map(day => ({
                                            ...day,
                                            date: new Date(day.date).toLocaleDateString(),
                                        }))}
                                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }}/>
                                        <YAxis tick={{ fontSize: 10 }}/>
                                        <Tooltip contentStyle={{ fontSize: 10 }}/>
                                        <Legend  wrapperStyle={{ fontSize: 10 }}/>
                                        <Line
                                            type="monotone"
                                            dataKey="total_income"
                                            name="Total Income"
                                            stroke="#000000"
                                            activeDot={{ r: 8 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="parts_cost"
                                            name="Parts Cost"
                                            stroke="#666666"
                                            strokeDasharray="5 5"
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="labor_income"
                                            name="Labor Income"
                                            stroke="#999999"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Repair data table */}
                        <div className="table-wrapper">
                            <h3>Daily Repair Details</h3>
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Repairs</th>
                                    <th>Parts Cost</th>
                                    <th>Labor Income</th>
                                    <th>Total Income</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentDailyRepairs.map((day, index) => (
                                    <tr key={index}>
                                        <td>{new Date(day.date).toLocaleDateString()}</td>
                                        <td>{day.total_repairs}</td>
                                        <td>Rs.{parseFloat(day.parts_cost).toFixed(2)}</td>
                                        <td>Rs.{parseFloat(day.labor_income).toFixed(2)}</td>
                                        <td>Rs.{parseFloat(day.total_income).toFixed(2)}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        {repairData.dailyRepairs.length > 0 && renderPagination(repairData.dailyRepairs.length)}
                    </div>
                ) : (
                    <div className="empty-state">
                        <p>Select a date range and click Generate to view repair report.</p>
                    </div>
                )}
            </>
        );
    };

    // Render content based on active tab
    const renderContent = () => {
        switch (activeTab) {
            case 'customers':
                return renderCustomerList();
            case 'products':
                return renderProductList();
            case 'sales':
                return renderSalesReport();
            case 'repairs':
                return renderRepairReport();
            default:
                return null;
        }
    };

    return (
        <div className="reports-page">
            {/* Header with date */}
            {/*<div className="page-header">*/}
            {/*    <h1>Reports</h1>*/}
            {/*</div>*/}

            {/* Navigation tabs */}
            {/* Navigation tabs - Fixed at the top */}
            <div className="report-tabs">
                <div className="tab-list">
                    <button
                        className={`tab-item ${activeTab === 'repairs' ? 'active' : ''}`}
                        onClick={() => setActiveTab('repairs')}
                    >
                        Repair Income
                    </button>
                    <button
                        className={`tab-item ${activeTab === 'sales' ? 'active' : ''}`}
                        onClick={() => setActiveTab('sales')}
                    >
                        Sales Revenue
                    </button>
                    <button
                        className={`tab-item ${activeTab === 'products' ? 'active' : ''}`}
                        onClick={() => setActiveTab('products')}
                    >
                        Product List
                    </button>
                    <button
                        className={`tab-item ${activeTab === 'customers' ? 'active' : ''}`}
                        onClick={() => setActiveTab('customers')}
                    >
                        Customer List
                    </button>
                </div>
            </div>


            {/* Report container */}
            <div className="report-container">
                {/*<div className="report-header">*/}
                {/*    <h2>{getReportTitle()}</h2>*/}
                {/*</div>*/}

                <div className="report-body">
                    {renderContent()}
                </div>
            </div>

            {/* Download URL notification if available */}
            {downloadURL && (
                <div className="download-notification">
                    <a href={downloadURL} className="btn btn-download">
                        Click Here to Download PDF
                    </a>
                </div>
            )}
        </div>
    );
};

export default ReportList;